import React, { useRef } from 'react';
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Button,
    HStack,
    Icon,
    Text,
} from '@chakra-ui/react';
import { AiOutlineQuestionCircle } from 'react-icons/ai';

/**
 * Diálogo PADRÃO de confirmação de AÇÃO (não-exclusão) — usar em TODO botão que
 * muda estado com um único clique (cancelar, concluir, reativar, devolver...),
 * para impedir ações disparadas por clique acidental (pedido do Lucas 2026-07-14:
 * "cancelei um negócio sem querer, apenas cliquei no botão").
 * Exclusões continuam usando o ConfirmDeleteDialog.
 *
 * Props:
 *   isOpen / onClose  → controle do diálogo (useDisclosure ou estado próprio)
 *   onConfirm         → callback executado ao confirmar a ação
 *   title             → título do diálogo (ex.: "Cancelar Negócio")
 *   itemName          → nome do item destacado na pergunta (ex.: "ERP + App")
 *   description       → texto adicional opcional após a pergunta padrão
 *   confirmLabel      → rótulo do botão de confirmação (ex.: "Sim, cancelar")
 *   scheme            → cor do botão confirmar: 'green' (padrão) | 'red' | 'amber'
 *   icon              → ícone do cabeçalho (padrão: AiOutlineQuestionCircle)
 *   isLoading         → estado de carregamento do botão confirmar
 */
const SCHEMES = {
    green: { bg: 'secondary', hover: 'secondaryHover', color: 'primary', accent: '#01E975' },
    red:   { bg: 'red.500', hover: 'red.600', color: 'white', accent: '#FC8181' },
    amber: { bg: '#B7791F', hover: '#8f5e18', color: 'white', accent: '#B7791F' },
};

const ConfirmActionDialog = ({
    isOpen,
    onClose,
    onConfirm,
    title = 'Confirmar Ação',
    itemName = '',
    description = '',
    confirmLabel = 'Confirmar',
    scheme = 'green',
    icon = AiOutlineQuestionCircle,
    isLoading = false,
}) => {
    const cancelRef = useRef(null);
    const cores = SCHEMES[scheme] || SCHEMES.green;

    return (
        <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={onClose}
            isCentered
            motionPreset="slideInBottom"
        >
            <AlertDialogOverlay backdropFilter="blur(4px)">
                <AlertDialogContent
                    bg="surface"
                    border="1px solid"
                    borderColor="borderPrimary"
                    color="textPrimary"
                    borderRadius="xl"
                >
                    <AlertDialogHeader
                        fontSize="lg"
                        fontWeight="900"
                        borderBottom="1px solid"
                        borderColor="borderPrimary"
                    >
                        <HStack spacing={2}>
                            <Icon as={icon} color={cores.accent} />
                            <Text>{title}</Text>
                        </HStack>
                    </AlertDialogHeader>
                    <AlertDialogBody py={5}>
                        <Text color="textSecondary">
                            Tem certeza que deseja continuar
                            {itemName ? (
                                <>
                                    {' '}com{' '}
                                    <Text as="span" fontWeight="800" color="textPrimary">
                                        "{itemName}"
                                    </Text>
                                </>
                            ) : ' com esta ação'}
                            ?
                        </Text>
                        {description && (
                            <Text color="textMuted" fontSize="sm" mt={2}>
                                {description}
                            </Text>
                        )}
                    </AlertDialogBody>
                    <AlertDialogFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
                        <Button
                            ref={cancelRef}
                            variant="ghost"
                            color="textSecondary"
                            _hover={{ bg: 'surfaceHover' }}
                            onClick={onClose}
                            size="sm"
                        >
                            Voltar
                        </Button>
                        <Button
                            bg={cores.bg}
                            color={cores.color}
                            _hover={{ bg: cores.hover }}
                            fontWeight="800"
                            onClick={onConfirm}
                            isLoading={isLoading}
                            size="sm"
                        >
                            {confirmLabel}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    );
};

export default ConfirmActionDialog;
